/**
 * Shared "right-panel" product form.
 *
 * Used by:
 *  - pages/partners/ImportFromUrlModal.jsx  (URL-import review step)
 *  - pages/partners/InventoryCreate.js      (manual Add New Product)
 *
 * The form operates on a single `product` object via an `onUpdate(patch)` prop.
 * The exact set of mandatory fields lives in `validateProduct()` so both flows
 * stay in sync.
 */
import React from 'react';
import { ExternalLink, Plus, Trash2 } from 'lucide-react';
import SizeChartEditor from '../SizeChartEditor';
import ColorChangeSection from '../ColorChangeSection';

// ---------------------------------------------------------------------------
// Constants — shared option lists
// ---------------------------------------------------------------------------
export const MOMENT_OPTIONS = [
  { value: 'creative_workday', label: 'Creative Workday' },
  { value: 'unwind', label: 'Unwind' },
  { value: 'after_hours', label: 'After Hours' },
  { value: 'escape', label: 'The Escape' },
  { value: 'gentle_celebration', label: 'Gentle Celebration' },
];
export const GENDERS = [
  { value: 'female', label: 'Women' },
  { value: 'male', label: 'Men' },
  { value: 'unisex', label: 'Unisex' },
];
export const DRESS_TYPES = [
  { value: 'top_wear', label: 'Top wear' },
  { value: 'bottom_wear', label: 'Bottom wear' },
  { value: 'full_dress', label: 'Full dress' },
  { value: 'accessory', label: 'Accessory' },
];

// ---------------------------------------------------------------------------
// Hex → human-readable colour name (CSS named-colour nearest match)
// ---------------------------------------------------------------------------
const NAMED_COLOURS = {
  black: '#000000', white: '#ffffff', red: '#ff0000', green: '#008000',
  blue: '#0000ff', yellow: '#ffff00', orange: '#ffa500', purple: '#800080',
  pink: '#ffc0cb', brown: '#a52a2a', gray: '#808080', cyan: '#00ffff',
  magenta: '#ff00ff', maroon: '#800000', navy: '#000080', olive: '#808000',
  teal: '#008080', silver: '#c0c0c0', gold: '#ffd700', beige: '#f5f5dc',
  ivory: '#fffff0', khaki: '#f0e68c', lavender: '#e6e6fa', salmon: '#fa8072',
  coral: '#ff7f50', turquoise: '#40e0d0', indigo: '#4b0082', violet: '#ee82ee',
  crimson: '#dc143c', tan: '#d2b48c', plum: '#dda0dd', mint: '#98ff98',
  charcoal: '#36454f', burgundy: '#800020', emerald: '#50c878', sapphire: '#0f52ba',
  cream: '#fffdd0', peach: '#ffe5b4', mustard: '#ffdb58',
};
const _hexToRgb = (hex) => {
  const h = (hex || '').replace('#', '');
  if (h.length !== 6) return null;
  const n = parseInt(h, 16);
  if (Number.isNaN(n)) return null;
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
export const colourNameFromHex = (hex) => {
  const rgb = _hexToRgb(hex);
  if (!rgb) return '';
  let best = '', bestD = Infinity;
  for (const [name, h] of Object.entries(NAMED_COLOURS)) {
    const r2 = _hexToRgb(h);
    const d = (rgb[0] - r2[0]) ** 2 + (rgb[1] - r2[1]) ** 2 + (rgb[2] - r2[2]) ** 2;
    if (d < bestD) { bestD = d; best = name; }
  }
  return best.charAt(0).toUpperCase() + best.slice(1);
};

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------
/**
 * Required: product_name, dress_type, gender, ≥1 color variant, selling_price,
 * size_chart (unless free_size), and a try-on image when there are 2+ photos.
 */
export const validateProduct = (p) => {
  const errors = {};
  if (!p?.product_name?.trim()) errors.product_name = 'Required';
  if (!p?.dress_type) errors.dress_type = 'Pick a dress type';
  if (!p?.gender) errors.gender = 'Required';
  if (!p?.color_variants?.length) errors.color_variants = 'Add at least one colour';
  const sp = Number(p?.selling_price);
  if (!sp || sp <= 0) errors.selling_price = 'Required';
  const sc = p?.size_chart;
  if (!p?.free_size) {
    const hasRows = sc && typeof sc === 'object' && Object.keys(sc).length > 0;
    if (!hasRows) errors.size_chart = 'Add a size chart or check Free Size';
  }
  const imgs = p?._selected_images || [];
  if (imgs.length > 1 && !p?._tryon_image_url) {
    errors.tryon_image_url = 'Pick a try-on image';
  }
  return errors;
};

export const isValid = (p) => Object.keys(validateProduct(p)).length === 0;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
export const Field = ({ label, required, error, children }) => (
  <div>
    <label className="block text-xs font-medium text-slate-700 mb-1">
      {label}{required && <span className="text-red-500"> *</span>}
    </label>
    {children}
    {error && <p className="text-[11px] text-red-500 mt-0.5">{error}</p>}
  </div>
);

export const ColorVariantEditor = ({ variants, onChange, error }) => {
  const add = () => {
    const defaultHex = '#000000';
    onChange([
      ...(variants || []),
      { name: colourNameFromHex(defaultHex), hex_code: defaultHex },
    ]);
  };
  const update = (idx, patch) => onChange(variants.map((v, i) => (i === idx ? { ...v, ...patch } : v)));
  const updateHex = (idx, hex) => {
    const v = variants[idx] || {};
    const wasAuto = !v.name || v.name === colourNameFromHex(v.hex_code || '');
    update(idx, {
      hex_code: hex,
      name: wasAuto ? colourNameFromHex(hex) : v.name,
    });
  };
  const remove = (idx) => onChange(variants.filter((_, i) => i !== idx));
  return (
    <div className="space-y-2" data-testid="color-variants-editor">
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-slate-700">
          Colour variants <span className="text-red-500">*</span>
        </label>
        <button type="button" onClick={add}
          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700">
          <Plus className="w-3 h-3" /> Add colour
        </button>
      </div>
      {(variants || []).length === 0 && (
        <p className="text-xs text-slate-400 italic">No colours yet — at least one is required.</p>
      )}
      <div className="space-y-1.5">
        {(variants || []).map((v, idx) => (
          <div key={idx} className="flex items-center gap-1.5 group">
            <input type="color" value={v.hex_code || '#000000'}
              onChange={(e) => updateHex(idx, e.target.value)}
              className="w-9 h-9 rounded-md border border-slate-200 cursor-pointer flex-shrink-0"
              data-testid={`color-variant-hex-${idx}`} />
            <input type="text" value={v.name || ''} placeholder="auto-filled from hex"
              onChange={(e) => update(idx, { name: e.target.value })}
              className="flex-1 px-2 py-1.5 border border-slate-200 rounded-md text-sm focus:ring-2 focus:ring-blue-500 outline-none"
              data-testid={`color-variant-name-${idx}`} />
            <input type="text" value={v.hex_code || ''} placeholder="#hex"
              onChange={(e) => updateHex(idx, e.target.value)}
              className="w-20 px-2 py-1.5 border border-slate-200 rounded-md text-xs font-mono focus:ring-2 focus:ring-blue-500 outline-none" />
            <button type="button" onClick={() => remove(idx)}
              className="p-1.5 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
};

export const MomentsSelector = ({ assigned, additional, onChange }) => {
  const selected = new Set([assigned, ...(additional || [])].filter(Boolean));
  const toggle = (val) => {
    const next = new Set(selected);
    if (next.has(val)) next.delete(val);
    else next.add(val);
    const arr = Array.from(next);
    onChange({
      assigned_moment: arr[0] || null,
      additional_moments: arr.slice(1),
    });
  };
  return (
    <div data-testid="moments-selector">
      <label className="text-xs font-medium text-slate-700 block mb-1.5">
        Moments <span className="text-slate-400 font-normal">
          (optional — you can leave all unselected)
        </span>
      </label>
      <div className="flex flex-wrap gap-1.5">
        {MOMENT_OPTIONS.map((m) => {
          const isOn = selected.has(m.value);
          return (
            <button key={m.value} type="button" onClick={() => toggle(m.value)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                isOn ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-slate-700 border-slate-200 hover:border-blue-400'}`}
              data-testid={`moment-${m.value}`}>
              {m.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main form panel
// ---------------------------------------------------------------------------
const InventoryProductForm = ({ product, onUpdate, errors = {} }) => {
  const update = (patch) => onUpdate(patch);
  return (
    <div className="overflow-y-auto h-full min-h-0 p-5 space-y-3.5" data-testid="inventory-product-form">
      {product.source_url && (
        <a href={product.source_url} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline">
          <ExternalLink className="w-3 h-3" /> View source
        </a>
      )}

      <Field label="Product name" required error={errors.product_name}>
        <input value={product.product_name || ''} onChange={(e) => update({ product_name: e.target.value })}
          className="ipf-input" data-testid="form-product-name" />
      </Field>

      <div className="grid grid-cols-2 gap-2">
        <Field label="SKU ID">
          <input value={product.sku_id || ''} onChange={(e) => update({ sku_id: e.target.value })}
            placeholder="auto-generated" className="ipf-input" data-testid="form-sku-id" />
        </Field>
        <Field label="Category">
          <input
            list="ipf-category-suggestions"
            value={product.category || ''}
            onChange={(e) => update({ category: e.target.value })}
            placeholder="e.g. Dresses, Cargo Sweatpants…"
            maxLength={100}
            className="ipf-input"
            data-testid="form-category"
          />
          <datalist id="ipf-category-suggestions">
            <option value="Dresses" /><option value="Sarees" /><option value="Kurtis" />
            <option value="Tops" /><option value="Ethnic Wear" /><option value="Western Wear" />
            <option value="Party Wear" /><option value="Casual Wear" /><option value="Formal Wear" />
            <option value="Bottom Wear" /><option value="Palazzos" /><option value="Other" />
          </datalist>
        </Field>
      </div>

      <Field label="Gender" required error={errors.gender}>
        <div className="flex gap-1.5" data-testid="form-gender">
          {GENDERS.map((g) => (
            <button key={g.value} type="button" onClick={() => update({ gender: g.value })}
              className={`flex-1 px-2.5 py-1.5 rounded-md text-xs font-medium border transition-all ${
                product.gender === g.value ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-slate-700 border-slate-200 hover:border-blue-400'}`}
              data-testid={`form-gender-${g.value}`}>
              {g.label}
            </button>
          ))}
        </div>
      </Field>

      <Field label="Dress type" required error={errors.dress_type}>
        <div className="grid grid-cols-2 gap-1.5" data-testid="form-dress-type">
          {DRESS_TYPES.map((d) => (
            <button key={d.value} type="button" onClick={() => update({ dress_type: d.value })}
              className={`px-2.5 py-1.5 rounded-md text-xs font-medium border transition-all ${
                product.dress_type === d.value ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-slate-700 border-slate-200 hover:border-blue-400'}`}
              data-testid={`dress-type-${d.value}`}>
              {d.label}
            </button>
          ))}
        </div>
      </Field>

      <ColorVariantEditor variants={product.color_variants || []}
        onChange={(v) => update({ color_variants: v })} error={errors.color_variants} />

      <ColorChangeSection
        variantCount={(product.color_variants || []).filter(v => v && v.hex_code).length}
        enabled={!!product.color_change_enabled}
        quality={product.color_change_quality || 'standard'}
        onChange={(patch) => update(patch)}
      />

      <div className="grid grid-cols-2 gap-2">
        <Field label="MRP">
          <input type="number" value={product.mrp ?? ''} onChange={(e) => update({ mrp: e.target.value === '' ? null : Number(e.target.value) })}
            className="ipf-input" data-testid="form-mrp" />
        </Field>
        <Field label="Selling price" required error={errors.selling_price}>
          <input type="number" value={product.selling_price ?? ''} onChange={(e) => update({ selling_price: e.target.value === '' ? null : Number(e.target.value) })}
            className="ipf-input" data-testid="form-selling-price" />
        </Field>
      </div>

      <Field label="Currency">
        <input value={product.currency || 'INR'} onChange={(e) => update({ currency: e.target.value })}
          className="ipf-input w-24" data-testid="form-currency" />
      </Field>

      <MomentsSelector assigned={product.assigned_moment} additional={product.additional_moments}
        onChange={(patch) => update(patch)} />

      {/* Size chart */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-slate-700">
            Size chart <span className="text-red-500">*</span>
          </label>
          <label className="inline-flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer">
            <input type="checkbox" checked={!!product.free_size}
              onChange={(e) => update({ free_size: e.target.checked, size_chart: e.target.checked ? null : product.size_chart })}
              className="rounded border-slate-300" data-testid="form-free-size" />
            Free size
          </label>
        </div>
        {!product.free_size && (
          <div className="border border-slate-200 rounded-lg p-2.5 bg-white">
            <SizeChartEditor value={product.size_chart} onChange={(v) => update({ size_chart: v })} />
          </div>
        )}
        {errors.size_chart && <p className="text-[11px] text-red-500">{errors.size_chart}</p>}
      </div>

      <Field label="Fabric">
        <input value={product.fabric || ''} onChange={(e) => update({ fabric: e.target.value })}
          className="ipf-input" data-testid="form-fabric" />
      </Field>

      <Field label="Description">
        <textarea value={product.description || ''} onChange={(e) => update({ description: e.target.value })}
          rows={2} className="ipf-input" data-testid="form-description" />
      </Field>

      <style>{`
        .ipf-input { width: 100%; padding: 0.45rem 0.6rem; border: 1px solid rgb(226 232 240);
          border-radius: 0.45rem; font-size: 0.8125rem; outline: none; background: white; }
        .ipf-input:focus { border-color: rgb(59 130 246); box-shadow: 0 0 0 2px rgb(191 219 254); }
      `}</style>
    </div>
  );
};

export default InventoryProductForm;
